/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Close, CloudUpload, Description, Image } from "@mui/icons-material"
import { Box, Button, Card, CardContent, Chip, IconButton, Modal, Typography } from "@mui/material"
import DocumentAPI from "api/document/document"
import React, { useEffect, useState } from "react"
import { normalizeFileName } from "utils/normalizeFileName"

interface DocumentComponentProps {
    formik?: any
    formikLabel?: string
    uuid?: string | null
    setUuid?: (uuid: string) => void
    podeSubstituir?: boolean,
    podeEnviar?: boolean
    title?: string
    subtitle?: string
}


export interface IDocument {
    uuid: string
    createdAt: string
    updatedAt: string
    deletedAt: any
    ownerUuid: any
    name: string
    documentType: string
    mimeType: string
    size: number
    base64: string
}


const DocumentComponent = ({
    formik,
    formikLabel,
    uuid,
    setUuid,
    podeSubstituir = false,
    podeEnviar = true,
    title,
    subtitle
}: DocumentComponentProps) => {
    const [document, setDocument] = useState<IDocument | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [uploading, setUploading] = useState(false)

    // Gerar um ID único para este componente específico
    const uniqueId = React.useMemo(() => `file-upload-input-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, [])

    const getDocument = async () => {
        if (!uuid) return

        try {
            const response = await DocumentAPI.getByUUID(uuid)
            const documentData = response.dados || response
            console.log('Documento carregado:', documentData) // Debug
            setDocument(documentData)
        } catch (error) {
            console.error("Erro ao buscar documento:", error)
        }
    }


    const handleUpload = async (file: File) => {
        if (!file) return

        setUploading(true)
        try {
            // Normalizar o nome do arquivo
            const normalizedName = normalizeFileName(file.name);
            console.log('Nome original:', file.name, '| Nome normalizado:', normalizedName);

            // Criar novo arquivo com nome normalizado
            const normalizedFile = new File([file], normalizedName, { type: file.type });

            const response = await DocumentAPI.upload({
                documentInfo: {
                    type: 'DOCUMENTO DE PARCEIRIA',
                },
                file: normalizedFile
            })
            setDocument(response as IDocument)
            setUuid && setUuid(response.uuid)
        } catch (error) {
            console.error("Erro ao fazer upload:", error)
        } finally {
            setUploading(false)
        }
    }

    const handleView = () => {
        setModalOpen(true)
    }

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const getFileIcon = (mimeType: string) => {
        const type = mimeType?.toLowerCase() || '';
        if (type.includes('image')) return <Image color="primary" />;
        if (type.includes('pdf')) return <Description color="error" />;
        return <Description color="action" />;
    }

    const getFileTypeChip = (mimeType: string) => {
        const type = mimeType?.toLowerCase() || '';
        if (type.includes('image')) return <Chip label="Imagem" color="primary" size="small" />;
        if (type.includes('pdf')) return <Chip label="PDF" color="error" size="small" />;
        return <Chip label="Documento" color="default" size="small" />;
    }

    const canPreview = (mimeType: string) => {
        const type = mimeType?.toLowerCase() || '';
        console.log('MimeType recebido:', mimeType, '| Convertido:', type); // Debug
        return type.includes('image') || type.includes('pdf');
    }

    useEffect(() => {
        if (uuid) {
            getDocument()
        }
    }, [uuid])

    return (
        <Box key={uuid}>
            {title && (
                <>
                    <Typography variant="h6" sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        mr: 2
                    }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}

                </>

            )}

            {document && (
                <Card sx={{
                    mb: 2,
                    width: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        borderColor: '#1976d2'
                    },
                    transition: 'all 0.3s ease'
                }}>

                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            {/* Ícone */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 56,
                                height: 56,
                                borderRadius: '12px',
                                bgcolor: document.mimeType?.toLowerCase().includes('pdf')
                                    ? 'rgba(244, 67, 54, 0.1)'
                                    : 'rgba(25, 118, 210, 0.1)'
                            }}>
                                {React.cloneElement(getFileIcon(document.mimeType), {
                                    sx: { fontSize: 32 }
                                })}
                            </Box>

                            {/* Informações do arquivo */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        mb: 0.5,
                                        color: '#1a1a1a'
                                    }}
                                >
                                    {document.name}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    {getFileTypeChip(document.mimeType)}
                                    <Typography variant="body2" color="text.secondary">
                                        {formatFileSize(document.size)}
                                    </Typography>
                                </Box>

                                <Typography variant="caption" color="text.secondary">
                                    {`Enviado em: ${new Date(document.createdAt).toLocaleString('pt-BR')}`}
                                    {document.updatedAt && document.updatedAt !== document.createdAt && (
                                        <>
                                            <br />
                                            {`Atualizado em: ${new Date(document.updatedAt).toLocaleString('pt-BR')}`}
                                        </>
                                    )}
                                </Typography>
                            </Box>

                            {/* Botões à direita */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                                {canPreview(document.mimeType) && (
                                    <Button
                                        onClick={handleView}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            minWidth: 80,
                                            height: 32,
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        Visualizar
                                    </Button>
                                )}

                                <Button
                                    onClick={() => {
                                        const link = window.document.createElement('a')
                                        link.href = `data:${document.mimeType};base64,${document.base64}`
                                        link.download = document.name
                                        link.click()
                                    }}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        minWidth: 80,
                                        height: 32,
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    Baixar
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {(!document || podeSubstituir) && podeEnviar && (
                <Box
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: '#fafafa',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: formik?.errors[formikLabel || ''] ? 'red' : '#1976d2',
                            backgroundColor: '#f0f7ff'
                        }
                    }}
                >
                    <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                                if (file.type !== "application/pdf") {
                                    alert("Apenas arquivos PDF são permitidos.");
                                    return;
                                }
                                handleUpload(file);
                            }
                        }}
                        style={{ display: 'none' }}
                        id={uniqueId}
                        disabled={uploading}
                    />

                    <label htmlFor={uniqueId} style={{ cursor: 'pointer' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CloudUpload sx={{ fontSize: 48, color: formik?.errors[formikLabel || ''] ? 'red' : '#1976d2' }} />

                            <Typography variant="h6" color={formik?.errors[formikLabel || ''] ? 'red' : 'primary'}>
                                {uploading
                                    ? 'Fazendo upload...'
                                    : document
                                        ? 'Clique para substituir o documento'
                                        : 'Clique para fazer upload'
                                }
                            </Typography>

                            <Typography variant="body2" color={formik?.errors[formikLabel || ''] ? 'red' : 'text.secondary'}>
                                {document ? 'Substituir documento atual' : 'Clique para selecionar o arquivo'}
                            </Typography>
                        </Box>
                    </label>
                </Box>
            )}

            {/* Mensagem quando não há documento e não pode enviar */}
            {!document && !podeEnviar && (
                <Box
                    sx={{
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: '#fafafa',
                        color: 'text.secondary'
                    }}
                >
                    <Description sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Nenhum documento disponível
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Não há documento anexado para visualização
                    </Typography>
                </Box>
            )}

            {/* Modal Simples */}
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90vw',
                        height: '90vh',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: '1px solid #e0e0e0',
                        flexShrink: 0
                    }}>
                        <Typography variant="h6" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            mr: 2
                        }}>
                            {document?.name}
                        </Typography>
                        <IconButton onClick={() => setModalOpen(false)}>
                            <Close />
                        </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <iframe
                            src={`data:${document?.mimeType};base64,${document?.base64}`}
                            width="100%"
                            height="100%"
                            style={{ border: 'none', display: 'block' }}
                            title={document?.name}
                        />
                    </Box>
                </Box>
            </Modal>
        </Box>
    )
}

export default DocumentComponent